import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, useDataTable, type DataTableFilter, type DataTableRefs } from '@odo/shared-ui';
import {
    useOneTimeDiscounts,
    useInfiniteOneTimeDiscounts,
    useDeleteOneTimeDiscount,
    useUnlinkOneTimeDiscount,
    usePracticeOptions
} from '../../hooks/useBilling';
import { DATA_TABLE_ROW_HEIGHT } from '../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../hooks/useViewportTableLimit';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useToast } from '../../providers/ToastProvider';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { apiErrorMessage } from '../../utils/apiError';
import { ChargeDrawer } from '../../components/billing/ChargeDrawer';
import { CreateOneTimeDiscountModal } from '../../components/billing/CreateOneTimeDiscountModal';
import { OneTimeDiscountStatusCards } from '../../components/billing/OneTimeDiscountStatusCards';
import { LinkOneTimeDiscountModal } from '../../components/billing/LinkOneTimeDiscountModal';
import { DeleteOneTimeDiscountModal } from '../../components/billing/one-time-discounts/DeleteOneTimeDiscountModal';
import {
    buildOneTimeDiscountColumns,
    type OneTimeDiscountsSortBy
} from '../../components/billing/one-time-discounts/oneTimeDiscountColumns';
import {
    BILLING_LIST_SEARCH_MAX_LENGTH,
    type OneTimeDiscountListItem,
    type OneTimeDiscountStatus
} from '../../services/billing';

const SEARCH_DEBOUNCE_MS = 300;
const STATUS_OPTIONS = ['available', 'expiring', 'applied', 'expired'] as const;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

const STATUS_FILTER_STORAGE_KEY = 'fv_billing_oneTimeDiscountStatusFilter';
const PRACTICE_FILTER_STORAGE_KEY = 'fv_billing_oneTimeDiscountPracticeFilter';

// Unlike charges/invoices, status here is a genuine multi-select: every
// checked value is sent to the endpoint, not just the first.
type OneTimeDiscountFilters = { status: OneTimeDiscountStatus[]; practiceId: string[] };

// Soonest expiry first, patient name A-Z first, newest creation first - the
// order applied when sorting moves to a different column.
const defaultOrderFor = (by: OneTimeDiscountsSortBy) =>
    by === 'expiry' || by === 'patient' ? ('asc' as const) : ('desc' as const);

export function OneTimeDiscountsList() {
    usePageTitle('One-time discounts');
    const { setTitle, setSubtitle } = usePageHeader();
    const { showToast } = useToast();
    const { hasPermission } = useMyPermissions();
    const canEditBilling = hasPermission('billing.update');

    const table = useDataTable<OneTimeDiscountsSortBy, OneTimeDiscountFilters>({
        defaultSort: { by: 'date', order: 'desc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { status: [], practiceId: [] },
        defaultOrderFor,
        filterStorageKeys: {
            status: STATUS_FILTER_STORAGE_KEY,
            practiceId: PRACTICE_FILTER_STORAGE_KEY
        }
    });
    const { status: statusFilter, practiceId: practiceFilter } = table.filters;
    const isInfinite = table.mode === 'infinite';

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [linkTarget, setLinkTarget] = useState<OneTimeDiscountListItem | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<OneTimeDiscountListItem | null>(null);
    const [openChargeId, setOpenChargeId] = useState<string | null>(null);

    const { mutateAsync: unlinkOneTimeDiscount } = useUnlinkOneTimeDiscount();
    const { mutateAsync: deleteOneTimeDiscount, isPending: isDeleting } =
        useDeleteOneTimeDiscount();

    const [searchInput, setSearchInput] = useState('');
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, commitSearch]);

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef
    } = useViewportTableLimit(table, { rowHeight: DATA_TABLE_ROW_HEIGHT });

    const { page, limit, sort, order } = table.params;
    const sharedDiscountParams = {
        limit,
        q: table.search || undefined,
        status: statusFilter,
        practiceId: practiceFilter[0],
        sort,
        order
    };
    // Both hooks are called unconditionally (React's rules of hooks) and only
    // one is ever `enabled` - same split as InvoicesContent's paged/infinite pair.
    const paged = useOneTimeDiscounts(
        { ...sharedDiscountParams, page },
        !isInfinite && pageSizeReady
    );
    const infinite = useInfiniteOneTimeDiscounts({ ...sharedDiscountParams, enabled: isInfinite });

    const oneTimeDiscounts = isInfinite ? infinite.oneTimeDiscounts : paged.oneTimeDiscounts;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isFetching = isInfinite ? infinite.isFetching : paged.isFetching;

    const { practices } = usePracticeOptions();
    const totalPages = Math.max(1, Math.ceil(meta.total / limit));

    const statusSummary = meta.statusSummary ?? {};
    const allCount = Object.values(statusSummary).reduce((sum, entry) => sum + entry.count, 0);

    useEffect(() => {
        setTitle('One-time discounts');
        setSubtitle(`${allCount} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [allCount, setTitle, setSubtitle]);

    const practiceOptions = useMemo(
        () =>
            practices.map((p) =>
                p.doctorName
                    ? {
                          value: p.id,
                          label: p.name,
                          sublabel: `Dr. ${p.doctorName}`,
                          keywords: [`Dr. ${p.doctorName}`, p.doctorName]
                      }
                    : { value: p.id, label: p.name }
            ),
        [practices]
    );

    // Open the charge in the sliding drawer without leaving the one-time discounts page.
    const openCharge = (chargeId: string) => setOpenChargeId(chargeId);

    const handleUnlink = useCallback(
        async (discount: OneTimeDiscountListItem) => {
            try {
                await unlinkOneTimeDiscount({ id: discount.id, chargeId: discount.chargeId });
                showToast('One-time discount unlinked, it is available again', 'success');
            } catch (error) {
                showToast(apiErrorMessage(error, 'Failed to unlink one-time discount'), 'error');
            }
        },
        [unlinkOneTimeDiscount, showToast]
    );

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteOneTimeDiscount({ id: deleteTarget.id });
            showToast('One-time discount deleted', 'success');
            setDeleteTarget(null);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to delete one-time discount'), 'error');
        }
    };

    const columns = useMemo(
        () =>
            buildOneTimeDiscountColumns({
                canEdit: canEditBilling,
                onLink: setLinkTarget,
                onDelete: setDeleteTarget,
                onOpenCharge: openCharge,
                onUnlink: (discount) => {
                    void handleUnlink(discount);
                }
            }),
        [handleUnlink, canEditBilling]
    );

    // Both pills keep `searchable: true` regardless of option count - the old
    // FilterDropdown always showed its in-menu search box, unlike the shared
    // dropdown's >8-option default.
    const filters: DataTableFilter[] = [
        {
            key: 'status',
            label: 'Status',
            multiple: true,
            options: STATUS_OPTIONS as unknown as string[],
            value: statusFilter,
            onChange: (value) => table.setFilter('status', value as OneTimeDiscountStatus[]),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        },
        {
            key: 'practiceId',
            label: 'Practice',
            multiple: true,
            options: practiceOptions,
            value: practiceFilter,
            onChange: (value) => table.setFilter('practiceId', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        }
    ];

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            {/* Status filter cards */}
            <div className="shrink-0">
                <OneTimeDiscountStatusCards
                    statusSummary={statusSummary}
                    selected={statusFilter}
                    onChange={(value) =>
                        table.setFilter('status', value as OneTimeDiscountStatus[])
                    }
                />
            </div>

            <DataTable
                label="One-time discounts"
                columns={columns}
                rows={oneTimeDiscounts}
                getRowKey={(discount) => discount.id}
                rowClassName={(discount) =>
                    discount.status === 'expiring' ? 'bg-yellow-50/40' : undefined
                }
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{ page, totalPages, total: meta.total, onPage: table.setPage }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                isLoading={isLoading}
                loadingRows={
                    <tr>
                        <td colSpan={11} className="px-3 py-8 text-center">
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                <span className="text-sm">Loading one-time discounts…</span>
                            </div>
                        </td>
                    </tr>
                }
                emptyState="No one-time discounts found."
                refs={refs}
                className="overflow-hidden px-8 pb-8"
                search={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Search reason, practice, patient, or charge #',
                    maxLength: BILLING_LIST_SEARCH_MAX_LENGTH
                }}
                filters={filters}
                toolbar={
                    isFetching && !isLoading ? (
                        <span className="text-xs text-gray-400">Refreshing…</span>
                    ) : undefined
                }
                actions={
                    canEditBilling
                        ? [
                              {
                                  key: 'new-one-time-discount',
                                  label: '+ New one-time discount',
                                  variant: 'primary',
                                  onClick: () => setShowCreateModal(true)
                              }
                          ]
                        : undefined
                }
            />

            {showCreateModal && (
                <CreateOneTimeDiscountModal onClose={() => setShowCreateModal(false)} />
            )}

            {linkTarget && (
                <LinkOneTimeDiscountModal
                    oneTimeDiscount={linkTarget}
                    onClose={() => setLinkTarget(null)}
                />
            )}

            {deleteTarget && (
                <DeleteOneTimeDiscountModal
                    discount={deleteTarget}
                    onConfirm={() => {
                        void handleDelete();
                    }}
                    onClose={() => setDeleteTarget(null)}
                    isPending={isDeleting}
                />
            )}

            {openChargeId && (
                <ChargeDrawer chargeId={openChargeId} onClose={() => setOpenChargeId(null)} />
            )}
        </div>
    );
}
